import { useRoleInfo } from "layouts/role-info/_context/RoleinfoContext";
import { Loader2 } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { handleAxiosAndCaughtError } from "utils";
import { FormInput } from "components/fields/FormInput";
import { FormTextArea } from "components/fields/FormTextArea";
import api from "api/axios";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "react-i18next";

const MainPage = () => {
  const { t } = useTranslation();
  const { roleInfo } = useRoleInfo();
  const { hasPermission } = useAuthContext();

  const permissions = {
    canUpdateRole: hasPermission(PERMISSIONS.UPDATE_ROLE),
  };

  const initialRoleData = useMemo(() => {
    return {
      name: roleInfo?.name || "",
      description: roleInfo?.description || "",
    };
  }, [roleInfo]);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
  }>(initialRoleData);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>(
    {}
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Check if form has changes
  useEffect(() => {
    const dataChanged =
      formData.name !== initialRoleData.name ||
      formData.description !== initialRoleData.description;
    setHasChanges(dataChanged);
  }, [formData, initialRoleData]);

  // Validate form
  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = t("roles.mainPage.nameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field: "name" | "description", value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // TODO: temporary for backward compatibility
      await api.put(`/groups/${roleInfo?.id}`, {
        name: formData.name,
        description: formData.description,
      });
      // Reset hasChanges after successful save
      setHasChanges(false);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if save button should be disabled
  const isSaveDisabled =
    !hasChanges ||
    !formData.name.trim() ||
    isLoading ||
    !permissions.canUpdateRole;

  return (
    <>
      <div className="space-y-6">
        {/* Name Field */}
        <FormInput
          label={t("roles.mainPage.nameLabel")}
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder={t("roles.mainPage.namePlaceholder")}
          required
          error={errors.name}
          disabled={isLoading || !permissions.canUpdateRole}
        />

        {/* Description Field */}
        <FormTextArea
          label={t("roles.mainPage.descriptionLabel")}
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder={t("roles.mainPage.descriptionPlaceholder")}
          rows={6}
          disabled={isLoading || !permissions.canUpdateRole}
          error={errors.description}
        />
      </div>
      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaveDisabled}
          className={`inline-flex min-w-[140px] items-center justify-center rounded-md px-6 py-3 text-sm font-medium transition-all duration-200 ${
            isSaveDisabled
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "bg-purple-600 text-white shadow-sm hover:bg-purple-700 hover:shadow-md active:bg-purple-800"
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" />
              {t("roles.mainPage.saving")}
            </>
          ) : (
            t("roles.mainPage.saveChanges")
          )}
        </button>
      </div>
    </>
  );
};

export default MainPage;
